/**
 * Sales Analytics Dashboard - Main Component
 * Premium analytics dashboard inspired by Shopify/Stripe/Vercel
 */
import React, { useState, useEffect, useMemo, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Calendar,
  ChevronLeft,
  ChevronRight,
  TrendingUp,
  TrendingDown,
  Store,
  Receipt,
  Wallet,
  CreditCard,
  Banknote,
  Smartphone,
  BarChart3,
  Lightbulb,
  RefreshCw,
  Loader2,
  Info,
  ShoppingBag,
  Minus,
} from "lucide-react";
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  PieChart as RechartsPie,
  Pie,
  Cell,
} from "recharts";

// Utility to get IST today's date
const getISTToday = () => {
  const now = new Date();
  const istOffset = 5.5 * 60 * 60 * 1000;
  const ist = new Date(now.getTime() + istOffset);
  return ist.toISOString().slice(0, 10);
};

// Date range presets
const DATE_PRESETS = [
  { key: "today", label: "Today", days: 0 },
  { key: "yesterday", label: "Yesterday", days: 1, offset: true },
  { key: "7d", label: "7D", days: 6 },
  { key: "30d", label: "30D", days: 29 },
  { key: "thisMonth", label: "This Month", type: "month" },
  { key: "lastMonth", label: "Last Month", type: "prevMonth" },
  { key: "custom", label: "Custom", type: "custom" },
];

// Colors for charts
const CHART_COLORS = {
  primary: "#9E6D5B",
  secondary: "#2D6A4F",
  tertiary: "#6366F1",
  quaternary: "#F59E0B",
  cash: "#22C55E",
  upi: "#8B5CF6",
  card: "#3B82F6",
  credit: "#F97316",
};

const PIE_COLORS = ["#22C55E", "#8B5CF6", "#3B82F6", "#F97316"];

// Format currency - always show full value, no compact formatting
const formatCurrency = (value) => {
  if (value === null || value === undefined) return "₹0";
  const num = Number(value);
  return `₹${num.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const formatFullCurrency = (value) => {
  if (value === null || value === undefined) return "₹0.00";
  return `₹${Number(value).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

// Get responsive font size based on value length
const getValueFontSize = (formattedValue) => {
  if (!formattedValue) return "text-2xl";
  const len = formattedValue.length;
  if (len <= 10) return "text-2xl";
  if (len <= 13) return "text-xl";
  if (len <= 16) return "text-lg";
  return "text-base";
};

// KPI Card Component - Premium design with responsive value display
function KPICard({ icon: Icon, label, value, change, previous, color = "default", loading, testId, tooltip }) {
  const isPositive = change >= 0;
  const changeColor = change === 0 ? "text-muted-foreground" : isPositive ? "text-emerald-600" : "text-red-600";
  const changeBgColor = change === 0 ? "bg-muted" : isPositive ? "bg-emerald-50" : "bg-red-50";
  const changeIcon = change === 0 ? <Minus className="w-3 h-3" /> : isPositive ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />;
  
  const colorStyles = {
    default: "border-[hsl(var(--border))] bg-white",
    primary: "border-[#9E6D5B]/30 bg-[#9E6D5B]/5",
    success: "border-emerald-200 bg-emerald-50/50",
    warning: "border-amber-200 bg-amber-50/50",
    danger: "border-red-200 bg-red-50/50",
  };

  const iconBgStyles = {
    default: "bg-muted",
    primary: "bg-[#9E6D5B]/10",
    success: "bg-emerald-100",
    warning: "bg-amber-100",
    danger: "bg-red-100",
  };

  const iconColorStyles = {
    default: "text-muted-foreground",
    primary: "text-[#9E6D5B]",
    success: "text-emerald-600",
    warning: "text-amber-600",
    danger: "text-red-600",
  };

  // Format the value for display
  const formattedValue = typeof value === "number" ? formatFullCurrency(value) : (value || "₹0.00");
  const fontSize = getValueFontSize(formattedValue);

  return (
    <Card 
      className={`p-5 min-h-[132px] rounded-2xl shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all duration-200 flex flex-col justify-between ${colorStyles[color]}`} 
      data-testid={testId}
      title={tooltip}
    >
      {/* Top Row: Icon + Change Badge */}
      <div className="flex items-start justify-between mb-3">
        <div className={`p-2.5 rounded-xl ${iconBgStyles[color]}`}>
          <Icon className={`w-4 h-4 ${iconColorStyles[color]}`} />
        </div>
        {!loading && change !== undefined && (
          <div className={`flex items-center gap-1 text-xs font-semibold px-2 py-1 rounded-full ${changeColor} ${changeBgColor}`}>
            {changeIcon}
            <span>{Math.abs(change).toFixed(1)}%</span>
          </div>
        )}
      </div>
      
      {/* Middle: Label */}
      <p className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground mb-1">{label}</p>
      
      {/* Bottom: Value */}
      {loading ? (
        <div className="h-8 w-full bg-muted animate-pulse rounded" />
      ) : (
        <p className={`${fontSize} font-bold tabular-nums leading-tight whitespace-nowrap text-foreground`}>
          {formattedValue}
        </p>
      )}
    </Card>
  );
}

// Chart type selector
function ChartTypeSelector({ value, onChange }) {
  return (
    <div className="flex gap-1 p-1 bg-muted rounded-lg">
      {[
        { key: "area", label: "Area" },
        { key: "line", label: "Line" },
        { key: "bar", label: "Bar" },
      ].map((type) => (
        <button
          key={type.key}
          onClick={() => onChange(type.key)}
          data-testid={`chart-type-${type.key}`}
          className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
            value === type.key
              ? "bg-white shadow-sm text-foreground"
              : "text-muted-foreground hover:text-foreground"
          }`}
        >
          {type.label}
        </button>
      ))}
    </div>
  );
}

// Metric toggle for chart
function MetricToggle({ value, onChange, options }) {
  return (
    <div className="flex flex-wrap gap-1.5">
      {options.map((opt) => (
        <button
          key={opt.key}
          onClick={() => onChange(opt.key)}
          data-testid={`metric-${opt.key}`}
          className={`px-2.5 py-1 text-xs font-medium rounded-full transition-colors border ${
            value === opt.key
              ? "bg-[#9E6D5B] text-white border-[#9E6D5B]"
              : "bg-white text-muted-foreground border-[hsl(var(--border))] hover:bg-muted"
          }`}
        >
          {opt.label}
        </button>
      ))}
    </div>
  );
}

// Custom Tooltip for charts
function CustomTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white/95 backdrop-blur-sm border border-[hsl(var(--border))] rounded-lg shadow-lg p-3">
      <p className="text-xs font-medium text-muted-foreground mb-2">{label}</p>
      {payload.map((item, idx) => (
        <div key={`${item.name}-${item.dataKey ?? idx}`} className="flex items-center gap-2 text-sm">
          <div className="w-2 h-2 rounded-full" style={{ backgroundColor: item.color }} />
          <span className="text-muted-foreground">{item.name}:</span>
          <span className="font-mono-data font-medium">{formatFullCurrency(item.value)}</span>
        </div>
      ))}
    </div>
  );
}

// Main Component
export default function SalesAnalyticsDashboard() {
  const today = useMemo(() => getISTToday(), []);
  
  // State
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [data, setData] = useState(null);
  const [shops, setShops] = useState([]);
  
  // Filters
  const [dateFrom, setDateFrom] = useState(today);
  const [dateTo, setDateTo] = useState(today);
  const [selectedPreset, setSelectedPreset] = useState("today");
  const [shopId, setShopId] = useState("");
  const [customRangeOpen, setCustomRangeOpen] = useState(false);
  
  // Chart options
  const [chartType, setChartType] = useState("area");
  const [chartMetric, setChartMetric] = useState("total");

  // Load shops
  useEffect(() => {
    api.get("/analytics/shops-list")
      .then((r) => setShops(r.data.shops || []))
      .catch(() => {});
  }, []);

  // Apply date preset
  const applyPreset = useCallback((preset) => {
    const t = new Date(today);
    let from, to;
    
    switch (preset) {
      case "today":
        from = to = today;
        break;
      case "yesterday":
        t.setDate(t.getDate() - 1);
        from = to = t.toISOString().slice(0, 10);
        break;
      case "7d":
        to = today;
        t.setDate(t.getDate() - 6);
        from = t.toISOString().slice(0, 10);
        break;
      case "30d":
        to = today;
        t.setDate(t.getDate() - 29);
        from = t.toISOString().slice(0, 10);
        break;
      case "thisMonth":
        from = `${today.slice(0, 7)}-01`;
        to = today;
        break;
      case "lastMonth":
        const lastMonth = new Date(t.getFullYear(), t.getMonth() - 1, 1);
        const lastMonthEnd = new Date(t.getFullYear(), t.getMonth(), 0);
        from = lastMonth.toISOString().slice(0, 10);
        to = lastMonthEnd.toISOString().slice(0, 10);
        break;
      case "custom":
        setCustomRangeOpen(true);
        return;
      default:
        return;
    }
    
    setDateFrom(from);
    setDateTo(to);
    setSelectedPreset(preset);
  }, [today]);

  // Navigate period
  const navigatePeriod = useCallback((direction) => {
    const from = new Date(dateFrom);
    const to = new Date(dateTo);
    const days = Math.ceil((to - from) / (1000 * 60 * 60 * 24)) + 1;
    
    if (direction === "prev") {
      from.setDate(from.getDate() - days);
      to.setDate(to.getDate() - days);
    } else {
      from.setDate(from.getDate() + days);
      to.setDate(to.getDate() + days);
    }
    
    // Don't go into future
    const todayDate = new Date(today);
    if (to > todayDate) {
      return;
    }
    
    setDateFrom(from.toISOString().slice(0, 10));
    setDateTo(to.toISOString().slice(0, 10));
    setSelectedPreset("");
  }, [dateFrom, dateTo, today]);

  // Load data
  const loadData = useCallback(async (isRefresh = false) => {
    if (isRefresh) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }
    
    try {
      const params = { date_from: dateFrom, date_to: dateTo };
      if (shopId) params.shop_id = shopId;
      
      const { data } = await api.get("/analytics/sales-dashboard", { params });
      setData(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [dateFrom, dateTo, shopId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Format date for display
  const formatDisplayDate = (dateStr) => {
    const d = new Date(dateStr);
    return d.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
  };

  // Get period label
  const getPeriodLabel = () => {
    if (dateFrom === dateTo) {
      return formatDisplayDate(dateFrom);
    }
    return `${formatDisplayDate(dateFrom)} - ${formatDisplayDate(dateTo)}`;
  };

  // Chart data with selected metric
  const chartData = useMemo(() => {
    if (!data?.series) return [];
    return data.series.map((item) => ({
      ...item,
      date: new Date(item.date).toLocaleDateString("en-IN", { day: "numeric", month: "short" }),
      Total: item.total,
      Retail: item.retail,
      Wholesale: item.wholesale,
    }));
  }, [data?.series]);

  const ChartComponent = chartType === "bar" ? BarChart : chartType === "line" ? LineChart : AreaChart;
  const DataComponent = chartType === "bar" ? Bar : chartType === "line" ? Line : Area;

  // Pie chart data
  const pieData = useMemo(() => {
    if (!data?.payment_breakdown) return [];
    return data.payment_breakdown.map((item, idx) => ({
      name: item.mode,
      value: item.amount,
      percentage: item.percentage,
      fill: PIE_COLORS[idx % PIE_COLORS.length],
    }));
  }, [data?.payment_breakdown]);

  return (
    <div className="space-y-6" data-testid="sales-analytics-dashboard">
      {/* Header */}
      <div className="mb-2">
        <h2 className="font-heading text-2xl tracking-tight flex items-center gap-2">
          <BarChart3 className="w-6 h-6 text-[#9E6D5B]" />
          Sales Analytics
        </h2>
        <p className="text-sm text-muted-foreground mt-1">
          Track sales performance, trends, payment breakdown, and business insights.
        </p>
      </div>

      {/* Control Bar */}
      <Card className="p-4 sticky top-0 z-30 bg-white/95 backdrop-blur-sm" data-testid="analytics-control-bar">
        <div className="flex flex-wrap items-center gap-3">
          {/* Period Navigation */}
          <div className="flex items-center gap-1">
            <Button
              variant="outline"
              size="icon"
              className="h-8 w-8"
              onClick={() => navigatePeriod("prev")}
              data-testid="period-prev"
            >
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <Button
              variant="outline"
              size="icon"
              className="h-8 w-8"
              onClick={() => navigatePeriod("next")}
              disabled={dateTo >= today}
              data-testid="period-next"
            >
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>

          {/* Current Period */}
          <div className="flex items-center gap-2 px-3 py-1.5 bg-muted rounded-lg">
            <Calendar className="w-4 h-4 text-muted-foreground" />
            <span className="text-sm font-medium" data-testid="period-label">{getPeriodLabel()}</span>
          </div>

          {/* Quick Range Pills */}
          <div className="flex flex-wrap gap-1.5">
            {DATE_PRESETS.map((preset) => (
              <button
                key={preset.key}
                onClick={() => applyPreset(preset.key)}
                className={`px-2.5 py-1 text-xs font-medium rounded-full transition-colors border ${
                  selectedPreset === preset.key
                    ? "bg-[#9E6D5B] text-white border-[#9E6D5B]"
                    : "bg-white text-muted-foreground border-[hsl(var(--border))] hover:bg-muted"
                }`}
                data-testid={`preset-${preset.key}`}
              >
                {preset.label}
              </button>
            ))}
          </div>

          {/* Spacer */}
          <div className="flex-1" />

          {/* Shop Filter */}
          <Select value={shopId || "all"} onValueChange={(v) => setShopId(v === "all" ? "" : v)}>
            <SelectTrigger className="w-[160px] h-8" data-testid="shop-filter">
              <Store className="w-4 h-4 mr-2 text-muted-foreground" />
              <SelectValue placeholder="All Shops" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Shops</SelectItem>
              {shops.map((shop) => (
                <SelectItem key={shop.id} value={shop.id}>
                  {shop.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Refresh */}
          <Button
            variant="outline"
            size="icon"
            className="h-8 w-8"
            onClick={() => loadData(true)}
            disabled={refreshing}
            data-testid="refresh-btn"
          >
            <RefreshCw className={`w-4 h-4 ${refreshing ? "animate-spin" : ""}`} />
          </Button>
        </div>
      </Card>

      {/* KPI Grid - 4 columns max for better value visibility */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="kpi-grid">
        <KPICard
          icon={ShoppingBag}
          label="Total Sales"
          value={data?.kpis?.total_sales?.value}
          change={data?.kpis?.total_sales?.change}
          color="primary"
          loading={loading}
          testId="kpi-total_sales"
        />
        <KPICard
          icon={TrendingUp}
          label="Retail"
          value={data?.kpis?.retail_sales?.value}
          change={data?.kpis?.retail_sales?.change}
          loading={loading}
          testId="kpi-retail_sales"
        />
        <KPICard
          icon={TrendingDown}
          label="Wholesale"
          value={data?.kpis?.wholesale_sales?.value}
          change={data?.kpis?.wholesale_sales?.change}
          loading={loading}
          testId="kpi-wholesale_sales"
        />
        <KPICard
          icon={Receipt}
          label="Invoices"
          value={data?.kpis?.invoice_count?.value}
          change={data?.kpis?.invoice_count?.change}
          loading={loading}
          testId="kpi-invoice_count"
        />
        <KPICard
          icon={Banknote}
          label="Cash"
          value={data?.kpis?.cash?.value}
          loading={loading}
          testId="kpi-cash"
        />
        <KPICard
          icon={Smartphone}
          label="Card + UPI"
          value={data?.kpis?.card_upi?.value}
          loading={loading}
          testId="kpi-card_upi"
        />
        <KPICard
          icon={CreditCard}
          label="Credit"
          value={data?.kpis?.credit?.value}
          loading={loading}
          testId="kpi-credit"
        />
        <KPICard
          icon={Wallet}
          label="Average Sales"
          value={(() => {
            // Calculate average sales = totalSales / numberOfBuckets
            const totalSales = data?.kpis?.total_sales?.value || 0;
            const buckets = data?.series?.length || 1;
            return totalSales / buckets;
          })()}
          change={data?.kpis?.avg_bill?.change}
          loading={loading}
          testId="kpi-avg_sales"
          tooltip="Average sales per selected period"
        />
      </div>

      {/* Main Chart */}
      <Card className="p-4 sm:p-6" data-testid="main-chart">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <div>
            <h3 className="font-heading text-lg tracking-tight">Sales Trend</h3>
            <p className="text-xs text-muted-foreground mt-0.5">Daily breakdown for selected period</p>
          </div>
          <div className="flex items-center gap-3">
            <MetricToggle
              value={chartMetric}
              onChange={setChartMetric}
              options={[
                { key: "total", label: "Total" },
                { key: "retail", label: "Retail" },
                { key: "wholesale", label: "Wholesale" },
              ]}
            />
            <ChartTypeSelector value={chartType} onChange={setChartType} />
          </div>
        </div>
        
        <div className="h-[300px] sm:h-[350px]" style={{ minWidth: 300 }}>
          {loading ? (
            <div className="h-full flex items-center justify-center">
              <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
            </div>
          ) : chartData.length === 0 ? (
            <div className="h-full flex flex-col items-center justify-center text-muted-foreground">
              <BarChart3 className="w-12 h-12 mb-2 opacity-50" />
              <p>No sales data found for the selected period.</p>
            </div>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <ChartComponent data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="colorTotal" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={CHART_COLORS.primary} stopOpacity={0.3} />
                    <stop offset="95%" stopColor={CHART_COLORS.primary} stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="colorRetail" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={CHART_COLORS.secondary} stopOpacity={0.3} />
                    <stop offset="95%" stopColor={CHART_COLORS.secondary} stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="colorWholesale" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={CHART_COLORS.tertiary} stopOpacity={0.3} />
                    <stop offset="95%" stopColor={CHART_COLORS.tertiary} stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#E8E6E1" vertical={false} />
                <XAxis
                  dataKey="date"
                  tick={{ fontSize: 11 }}
                  stroke="#827C7A"
                  axisLine={false}
                  tickLine={false}
                />
                <YAxis
                  tick={{ fontSize: 11 }}
                  stroke="#827C7A"
                  axisLine={false}
                  tickLine={false}
                  tickFormatter={(v) => formatCurrency(v)}
                />
                <Tooltip content={<CustomTooltip />} />
                {chartType === "area" ? (
                  <>
                    {chartMetric === "total" && (
                      <Area
                        type="monotone"
                        dataKey="Total"
                        stroke={CHART_COLORS.primary}
                        strokeWidth={2}
                        fill="url(#colorTotal)"
                      />
                    )}
                    {chartMetric === "retail" && (
                      <Area
                        type="monotone"
                        dataKey="Retail"
                        stroke={CHART_COLORS.secondary}
                        strokeWidth={2}
                        fill="url(#colorRetail)"
                      />
                    )}
                    {chartMetric === "wholesale" && (
                      <Area
                        type="monotone"
                        dataKey="Wholesale"
                        stroke={CHART_COLORS.tertiary}
                        strokeWidth={2}
                        fill="url(#colorWholesale)"
                      />
                    )}
                  </>
                ) : chartType === "line" ? (
                  <>
                    {chartMetric === "total" && (
                      <Line type="monotone" dataKey="Total" stroke={CHART_COLORS.primary} strokeWidth={2} dot={false} />
                    )}
                    {chartMetric === "retail" && (
                      <Line type="monotone" dataKey="Retail" stroke={CHART_COLORS.secondary} strokeWidth={2} dot={false} />
                    )}
                    {chartMetric === "wholesale" && (
                      <Line type="monotone" dataKey="Wholesale" stroke={CHART_COLORS.tertiary} strokeWidth={2} dot={false} />
                    )}
                  </>
                ) : (
                  <>
                    {chartMetric === "total" && (
                      <Bar dataKey="Total" fill={CHART_COLORS.primary} radius={[4, 4, 0, 0]} />
                    )}
                    {chartMetric === "retail" && (
                      <Bar dataKey="Retail" fill={CHART_COLORS.secondary} radius={[4, 4, 0, 0]} />
                    )}
                    {chartMetric === "wholesale" && (
                      <Bar dataKey="Wholesale" fill={CHART_COLORS.tertiary} radius={[4, 4, 0, 0]} />
                    )}
                  </>
                )}
              </ChartComponent>
            </ResponsiveContainer>
          )}
        </div>
      </Card>

      {/* Secondary Charts Row */}
      <div className="grid md:grid-cols-2 gap-4">
        {/* Payment Breakdown - Donut Chart */}
        <Card className="p-4 sm:p-6" data-testid="payment-breakdown">
          <h3 className="font-heading text-base tracking-tight mb-4">Payment Method Breakdown</h3>
          {loading ? (
            <div className="h-[200px] flex items-center justify-center">
              <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
            </div>
          ) : pieData.length === 0 ? (
            <div className="h-[200px] flex items-center justify-center text-sm text-muted-foreground">
              No payment data
            </div>
          ) : (
            <div className="flex items-center gap-4">
              <div className="w-[160px] h-[160px]">
                <ResponsiveContainer width="100%" height="100%">
                  <RechartsPie>
                    <Pie
                      data={pieData}
                      cx="50%"
                      cy="50%"
                      innerRadius={45}
                      outerRadius={70}
                      paddingAngle={2}
                      dataKey="value"
                    >
                      {pieData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.fill} />
                      ))}
                    </Pie>
                    <Tooltip
                      formatter={(value) => formatFullCurrency(value)}
                      contentStyle={{ fontSize: 12 }}
                    />
                  </RechartsPie>
                </ResponsiveContainer>
              </div>
              <div className="flex-1 space-y-2">
                {pieData.map((item, idx) => (
                  <div key={`${item.name}-${idx}`} className="flex items-center justify-between text-sm">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full" style={{ backgroundColor: item.fill }} />
                      <span className="text-muted-foreground">{item.name}</span>
                    </div>
                    <div className="text-right">
                      <span className="font-mono-data font-medium">{formatCurrency(item.value)}</span>
                      <span className="text-xs text-muted-foreground ml-2">({item.percentage}%)</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </Card>

        {/* Shop Comparison - Horizontal Bar */}
        <Card className="p-4 sm:p-6" data-testid="shop-comparison">
          <h3 className="font-heading text-base tracking-tight mb-4">Shop-wise Sales</h3>
          {loading ? (
            <div className="h-[200px] flex items-center justify-center">
              <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
            </div>
          ) : !data?.shop_comparison?.length ? (
            <div className="h-[200px] flex items-center justify-center text-sm text-muted-foreground">
              No shop data
            </div>
          ) : (
            <div className="h-[200px]" style={{ minWidth: 200 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={data.shop_comparison.slice(0, 5)}
                  layout="vertical"
                  margin={{ top: 0, right: 20, left: 0, bottom: 0 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#E8E6E1" horizontal={false} />
                  <XAxis type="number" tick={{ fontSize: 10 }} tickFormatter={(v) => formatCurrency(v)} />
                  <YAxis
                    type="category"
                    dataKey="shop_name"
                    tick={{ fontSize: 11 }}
                    width={80}
                    axisLine={false}
                    tickLine={false}
                  />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="total" name="Total" fill={CHART_COLORS.primary} radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </Card>
      </div>

      {/* Insight Cards Row - All in one row */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
        {/* Income */}
        <Card className="p-4 border-emerald-200 bg-emerald-50/30" data-testid="insight-income">
          <div className="flex items-center gap-2 mb-2">
            <div className="p-1.5 rounded bg-emerald-100">
              <TrendingUp className="w-4 h-4 text-emerald-600" />
            </div>
            <span className="text-xs uppercase tracking-widest text-muted-foreground">Income</span>
          </div>
          {loading ? (
            <div className="h-6 w-24 bg-emerald-100 animate-pulse rounded" />
          ) : (
            <p className="text-xl font-semibold font-mono-data text-emerald-700">
              {formatFullCurrency(data?.kpis?.income?.value || 0)}
            </p>
          )}
        </Card>

        {/* Expenses */}
        <Card className="p-4 border-red-200 bg-red-50/30" data-testid="insight-expenses">
          <div className="flex items-center gap-2 mb-2">
            <div className="p-1.5 rounded bg-red-100">
              <TrendingDown className="w-4 h-4 text-red-600" />
            </div>
            <span className="text-xs uppercase tracking-widest text-muted-foreground">Expenses</span>
          </div>
          {loading ? (
            <div className="h-6 w-24 bg-red-100 animate-pulse rounded" />
          ) : (
            <p className="text-xl font-semibold font-mono-data text-red-700">
              {formatFullCurrency(data?.kpis?.expenses?.value || 0)}
            </p>
          )}
        </Card>

        {/* Best Shop */}
        <Card className="p-4" data-testid="insight-best-shop">
          <div className="flex items-center gap-2 mb-2">
            <div className="p-1.5 rounded bg-muted">
              <Store className="w-4 h-4 text-muted-foreground" />
            </div>
            <span className="text-xs uppercase tracking-widest text-muted-foreground">Best Shop</span>
          </div>
          {loading ? (
            <div className="space-y-1">
              <div className="h-5 w-20 bg-muted animate-pulse rounded" />
              <div className="h-4 w-16 bg-muted animate-pulse rounded" />
            </div>
          ) : data?.summary?.best_shop ? (
            <>
              <p className="font-semibold">{data.summary.best_shop.shop_name}</p>
              <p className="text-sm text-muted-foreground font-mono-data">
                {formatFullCurrency(data.summary.best_shop.total)}
              </p>
            </>
          ) : (
            <p className="text-sm text-muted-foreground">No data</p>
          )}
        </Card>

        {/* Best Day */}
        <Card className="p-4" data-testid="insight-best-day">
          <div className="flex items-center gap-2 mb-2">
            <div className="p-1.5 rounded bg-muted">
              <Calendar className="w-4 h-4 text-muted-foreground" />
            </div>
            <span className="text-xs uppercase tracking-widest text-muted-foreground">Highest Sales Day</span>
          </div>
          {loading ? (
            <div className="space-y-1">
              <div className="h-5 w-24 bg-muted animate-pulse rounded" />
              <div className="h-4 w-16 bg-muted animate-pulse rounded" />
            </div>
          ) : data?.summary?.best_day ? (
            <>
              <p className="font-semibold">
                {new Date(data.summary.best_day.date).toLocaleDateString("en-IN", {
                  weekday: "long",
                  day: "numeric",
                  month: "short",
                })}
              </p>
              <p className="text-sm text-muted-foreground font-mono-data">
                {formatFullCurrency(data.summary.best_day.total)}
              </p>
            </>
          ) : (
            <p className="text-sm text-muted-foreground">No data</p>
          )}
        </Card>

        {/* Worst Day */}
        <Card className="p-4" data-testid="insight-worst-day">
          <div className="flex items-center gap-2 mb-2">
            <div className="p-1.5 rounded bg-muted">
              <Calendar className="w-4 h-4 text-muted-foreground" />
            </div>
            <span className="text-xs uppercase tracking-widest text-muted-foreground">Lowest Sales Day</span>
          </div>
          {loading ? (
            <div className="space-y-1">
              <div className="h-5 w-24 bg-muted animate-pulse rounded" />
              <div className="h-4 w-16 bg-muted animate-pulse rounded" />
            </div>
          ) : data?.summary?.worst_day ? (
            <>
              <p className="font-semibold">
                {new Date(data.summary.worst_day.date).toLocaleDateString("en-IN", {
                  weekday: "long",
                  day: "numeric",
                  month: "short",
                })}
              </p>
              <p className="text-sm text-muted-foreground font-mono-data">
                {formatFullCurrency(data.summary.worst_day.total)}
              </p>
            </>
          ) : (
            <p className="text-sm text-muted-foreground">No data</p>
          )}
        </Card>
      </div>

      {/* Top Brands Table */}
      <Card className="overflow-hidden" data-testid="top-brands-table">
        <div className="p-4 border-b border-[hsl(var(--border))]">
          <h3 className="font-heading text-base tracking-tight">Top Brands</h3>
          <p className="text-xs text-muted-foreground mt-0.5">Ranked by total sales value</p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/50">
              <tr className="text-left">
                <th className="p-3 text-xs uppercase tracking-wider text-muted-foreground font-medium w-12">#</th>
                <th className="p-3 text-xs uppercase tracking-wider text-muted-foreground font-medium">Brand</th>
                <th className="p-3 text-xs uppercase tracking-wider text-muted-foreground font-medium text-right">Retail</th>
                <th className="p-3 text-xs uppercase tracking-wider text-muted-foreground font-medium text-right">Wholesale</th>
                <th className="p-3 text-xs uppercase tracking-wider text-muted-foreground font-medium text-right">Units</th>
                <th className="p-3 text-xs uppercase tracking-wider text-muted-foreground font-medium text-right">Total</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                Array.from({ length: 5 }).map((_, i) => (
                  <tr key={`brand-skel-${i}`} className="border-t border-[hsl(var(--border))]">
                    <td className="p-3"><div className="h-4 w-4 bg-muted animate-pulse rounded" /></td>
                    <td className="p-3"><div className="h-4 w-24 bg-muted animate-pulse rounded" /></td>
                    <td className="p-3"><div className="h-4 w-16 bg-muted animate-pulse rounded ml-auto" /></td>
                    <td className="p-3"><div className="h-4 w-16 bg-muted animate-pulse rounded ml-auto" /></td>
                    <td className="p-3"><div className="h-4 w-10 bg-muted animate-pulse rounded ml-auto" /></td>
                    <td className="p-3"><div className="h-4 w-20 bg-muted animate-pulse rounded ml-auto" /></td>
                  </tr>
                ))
              ) : !data?.top_brands?.length ? (
                <tr>
                  <td colSpan={6} className="p-8 text-center text-muted-foreground">
                    No brand data for this period.
                  </td>
                </tr>
              ) : (
                data.top_brands.map((brand) => (
                  <tr
                    key={brand.rank}
                    className="border-t border-[hsl(var(--border))] hover:bg-muted/30 transition-colors"
                  >
                    <td className="p-3 font-mono-data text-muted-foreground">{brand.rank}</td>
                    <td className="p-3 font-medium">{brand.brand}</td>
                    <td className="p-3 text-right font-mono-data text-emerald-700">
                      {formatFullCurrency(brand.retail)}
                    </td>
                    <td className="p-3 text-right font-mono-data text-blue-700">
                      {formatFullCurrency(brand.wholesale)}
                    </td>
                    <td className="p-3 text-right font-mono-data">{brand.units}</td>
                    <td className="p-3 text-right font-mono-data font-semibold">
                      {formatFullCurrency(brand.total)}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Shop Sales Table */}
      <Card className="overflow-hidden" data-testid="shop-sales-table">
        <div className="p-4 border-b border-[hsl(var(--border))]">
          <h3 className="font-heading text-base tracking-tight">Shop-wise Sales</h3>
          <p className="text-xs text-muted-foreground mt-0.5">Breakdown by location</p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/50">
              <tr className="text-left">
                <th className="p-3 text-xs uppercase tracking-wider text-muted-foreground font-medium">Shop</th>
                <th className="p-3 text-xs uppercase tracking-wider text-muted-foreground font-medium text-right">Retail</th>
                <th className="p-3 text-xs uppercase tracking-wider text-muted-foreground font-medium text-right">Wholesale</th>
                <th className="p-3 text-xs uppercase tracking-wider text-muted-foreground font-medium text-right">Invoices</th>
                <th className="p-3 text-xs uppercase tracking-wider text-muted-foreground font-medium text-right">Total</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                Array.from({ length: 3 }).map((_, i) => (
                  <tr key={`shop-skel-${i}`} className="border-t border-[hsl(var(--border))]">
                    <td className="p-3"><div className="h-4 w-20 bg-muted animate-pulse rounded" /></td>
                    <td className="p-3"><div className="h-4 w-16 bg-muted animate-pulse rounded ml-auto" /></td>
                    <td className="p-3"><div className="h-4 w-16 bg-muted animate-pulse rounded ml-auto" /></td>
                    <td className="p-3"><div className="h-4 w-10 bg-muted animate-pulse rounded ml-auto" /></td>
                    <td className="p-3"><div className="h-4 w-20 bg-muted animate-pulse rounded ml-auto" /></td>
                  </tr>
                ))
              ) : !data?.shop_comparison?.length ? (
                <tr>
                  <td colSpan={5} className="p-8 text-center text-muted-foreground">
                    No shop data for this period.
                  </td>
                </tr>
              ) : (
                data.shop_comparison.map((shop) => (
                  <tr
                    key={shop.shop_id}
                    className="border-t border-[hsl(var(--border))] hover:bg-muted/30 transition-colors"
                  >
                    <td className="p-3 font-medium">{shop.shop_name}</td>
                    <td className="p-3 text-right font-mono-data text-emerald-700">
                      {formatFullCurrency(shop.retail)}
                    </td>
                    <td className="p-3 text-right font-mono-data text-blue-700">
                      {formatFullCurrency(shop.wholesale)}
                    </td>
                    <td className="p-3 text-right font-mono-data">{shop.count}</td>
                    <td className="p-3 text-right font-mono-data font-semibold">
                      {formatFullCurrency(shop.total)}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Performance Insights Panel */}
      {data?.insights?.length > 0 && (
        <Card className="p-4 sm:p-6 bg-blue-50/50 border-blue-200" data-testid="insights-panel">
          <div className="flex items-center gap-2 mb-3">
            <div className="p-1.5 rounded bg-blue-100">
              <Lightbulb className="w-4 h-4 text-blue-600" />
            </div>
            <h3 className="font-heading text-base tracking-tight">Performance Insights</h3>
          </div>
          <ul className="space-y-2">
            {data.insights.map((insight, idx) => (
              <li key={`insight-${idx}-${String(insight).slice(0, 20)}`} className="flex items-start gap-2 text-sm text-blue-900">
                <Info className="w-4 h-4 text-blue-500 mt-0.5 flex-shrink-0" />
                <span>{insight}</span>
              </li>
            ))}
          </ul>
        </Card>
      )}

      {/* Custom Date Range Dialog */}
      <Dialog open={customRangeOpen} onOpenChange={setCustomRangeOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Custom Date Range</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div>
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Start Date</Label>
              <Input
                type="date"
                value={dateFrom}
                max={today}
                onChange={(e) => setDateFrom(e.target.value)}
                className="mt-1"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">End Date</Label>
              <Input
                type="date"
                value={dateTo}
                max={today}
                min={dateFrom}
                onChange={(e) => setDateTo(e.target.value)}
                className="mt-1"
              />
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setCustomRangeOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={() => {
                setSelectedPreset("custom");
                setCustomRangeOpen(false);
              }}
              style={{ backgroundColor: "#9E6D5B" }}
            >
              Apply
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
